import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Stack,
  Table,
  Tabs,
  Tab,
  Box,
  Typography,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  TableHeadCustom,
  TableNoData,
  useTable,
} from '../../../components/table';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import Scrollbar from '../../../components/scrollbar';
import SignatureDialog from '../../../components/signature/SignatureDialog';
import { CustomAvatar } from '../../../components/custom-avatar';
import PatientInfo from './PatientInfo';
import {
  ClinicalRecordLockBridge,
  ClinicalRecordLockProvider,
  ClinicalRecordLockValue,
  ClinicalRecordStatusBar,
  ClinicalTabPanels,
} from '../../../components/clinical-record-lock';
import Label from '../../../components/label';
import PatientPrescreening from './PatientPrescreening';
import PatientCommunicationLog from './PatientCommunicationLog';
import PatientNhsClaims from './PatientNhsClaims';
import { ClinicalRecords } from '../../../api/clinicalRecords.service';
import { formatDate } from '@fullcalendar/core';
import { fDate } from '../../../utils/formatTime';
import Refraction from './Refraction/Refraction';
import AddHistoryAndSymptoms from './HistoryAndSymptoms/AddHistoryAndSymptoms';
import { useParams, useLocation } from 'react-router-dom';
import { PatientService } from '../../../api/patient.service';
import { HistoryAndSymptomsService } from '../../../api/historyAndSymptoms.service';
import Examination from './Examination/Examination';
import Handover from './Handover/Handover';
import { HandoverService } from '../../../api/handover.service';
import CLHistoryAndSymptoms from './ContactLenses/CLHistoryAndSymptoms/CLHistoryAndSymptoms';
import ClinicalRecordTypeSelect from './ContactLenses/CLHistoryAndSymptoms/Type';
import AnteriorEye from './ContactLenses/AnteriorEye/AnteriorEye';
import CLFitting from './ContactLenses/CLFitting/CLFitting';
import CLAftercare from './ContactLenses/CLAftercare/CLAftercare';
import TeachScreen from './ContactLenses/TeachScreen/TeachScreen';
import { ICLHistoryAndSymptoms } from '../../../@types/clHistoryAndSymptoms.type';
import { CLHistoryAndSymptomsService } from '../../../api/contact-lenses/cl-history-and-symptoms.service';
import { IClinicalRecordSchema } from '../../../@types/clinicalRecord.type';
import { IRefractionSchema } from '../../../@types/refraction';
import { IExaminationSchema } from '../../../@types/examination';
import { IHandoverSchema } from '../../../@types/handover';
import { IPatientSymptomsSchema } from '../../../@types/patientSymptoms';
import { IPatientInfo } from '../../../@types/patientInfo.type';
import { AnteriorEyeService } from '../../../api/contact-lenses/anterior-eye.service';
import { IAnteriorEye } from '../../../@types/anteriorEye.type';
import CLHandover from './ContactLenses/Handover/CLHandover';
import { CLFittingService } from '../../../api/contact-lenses/cl-fitting.service';
import { ICLFitting } from '../../../@types/clFitting.type';
import { TrailsService } from '../../../api/contact-lenses/trail.service';
import { ITrail } from '../../../@types/trail.type';
import { CLAftercareService } from '../../../api/contact-lenses/cl-aftercare.service';
import { ICLAftercare } from '../../../@types/clAftercare.type';
import { CLHandoverService } from '../../../api/contact-lenses/cl-handover.service';
import { ICLHandover } from '../../../@types/clHandover.type';
import { TeachScreenService } from '../../../api/contact-lenses/teachscreen.service';
import { ITeachScreen } from '../../../@types/teachscreen.type';
import {
  IAutoRefractorSchema,
  IVisionSchema,
} from '../../../@types/specsHistory';
import { useAppContext } from '../../../providers/AppContext';
import DiabeticEyeScreeningForm from './ClinalPathways/DiabeticEyeScreeningForm';
import MecsCuesForm from './ClinalPathways/MecsCuesForm';
import GlaucomaReferralRefinementForm from './ClinalPathways/GlaucomaReferralRefinementForm';
import ContactLensTeachingForm from './ClinalPathways/ContactLensTeachingForm';
import HandoverOutcomeForm from './ClinalPathways/HandoverOutcomeForm';
import { DEService } from '../../../api/clinical-pathways/des.service';
import { MecsCuesService } from '../../../api/clinical-pathways/mecs-cues.service';
import { GlaucomaReferralRefinementService } from '../../../api/clinical-pathways/grr.service';

const EYE_EXAMINATION_TABS = [
  { id: 'info', label: 'Info', align: 'left' },
  { id: 'prescreening', label: 'Prescreening', align: 'left' },
  { id: 'history_and_symptoms', label: 'History And Symptoms', align: 'left' },
  {
    id: 'refraction',
    label: 'Refraction',
    align: 'left',
  },
  { id: 'examination', label: 'Examination', align: 'center' },
  { id: 'handover', label: 'Handover', align: 'center' },
  { id: 'communications', label: 'Communications', align: 'left' },
  { id: 'nhs_claims', label: 'NHS Claims', align: 'left' },
];

const CONTACT_LENSES_TABS = [
  { id: 'info', label: 'Info', align: 'left' },
  { id: 'cl_h_and_s', label: 'CL H&S', align: 'left' },
  { id: 'anterior_eye', label: 'Anterior Eye', align: 'left' },
  {
    id: 'cl_fitting',
    label: 'CL Fitting',
    align: 'left',
  },
  { id: 'cl_aftercare', label: 'CL Aftercare', align: 'center' },
  { id: 'cl_handover', label: 'Handover', align: 'center' },
  { id: 'teach_screen', label: 'Teach Screen', align: 'center' },
  { id: 'communications', label: 'Communications', align: 'left' },
];

const CLINICAL_PATHWAY_TABS = [
  { id: 'info', label: 'Info', align: 'left' },
  { id: 'des', label: 'Diabetic Eye Screening' },
  { id: 'mecs', label: 'MECS / CUES' },
  { id: 'grr', label: 'Glaucoma Referral Refinement' },
  // { id: "cl_teaching", label: "Contact Lens Teaching" },
  { id: 'handover_outcome', label: 'Handover & Outcome', align: 'center' },
  { id: 'communications', label: 'Communications', align: 'left' },
];

const TABLE_HEAD = [
  { id: 'id', label: 'ID', align: 'left' },
  {
    id: 'reasonForVisit',
    label: 'Visit Reason',
    align: 'left',
  },
  {
    id: 'type',
    label: 'Type',
    align: 'left',
  },
  { id: 'createdDate', label: 'Created Date', align: 'left' },
];

function formatVisitReasonDisplay(raw?: string | null): string {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(', ');
}

export default function PatientClinicalRecords() {
  // ------------------------------
  // 📌 TABLE & ROUTER
  // ------------------------------
  const { dense } = useTable({ defaultOrderBy: 'startTime' });
  const location = useLocation();
  const { patientId } = useParams();

  // ------------------------------
  // 📌 CONTEXT
  // ------------------------------
  const { getAllClinicalRecords, clinicalRecords } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isRefreshingRecords, setIsRefreshingRecords] = useState(false);
  const [isRecordLoading, setIsRecordLoading] = useState(false);
  // Counts in-flight section loads (row click + post-refresh reload) so the
  // loader stays on until every one of them has finished.
  const pendingLoadsRef = useRef(0);
  // Identifies the most recent open request. A click during an in-flight load
  // (e.g. the refresh that follows creating a record) must win rather than be
  // dropped, or the loader appears and the record never opens.
  const openSeqRef = useRef(0);
  const trackRecordLoad = useCallback(
    async <T,>(work: () => Promise<T>): Promise<T> => {
      pendingLoadsRef.current += 1;
      setIsRecordLoading(true);
      try {
        return await work();
      } finally {
        pendingLoadsRef.current -= 1;
        if (pendingLoadsRef.current === 0) setIsRecordLoading(false);
      }
    },
    [],
  );
  const refreshClinicalRecords = useCallback(async () => {
    if (!patientId) return Promise.resolve([]);
    setIsRefreshingRecords(true);
    try {
      return await getAllClinicalRecords(patientId);
    } finally {
      setIsRefreshingRecords(false);
    }
  }, [patientId, getAllClinicalRecords]);

  // ------------------------------
  // 📌 PRIMARY UI STATE
  // ------------------------------
  const [eyeExamination, setEyeExamination] = useState('info');
  const [contactLenses, setContactLenses] = useState('info');
  const [diabeticEyeScreening, setDiabeticEyeScreening] = useState('info');
  const [selectedType, setSelectedType] = useState<string>('eye_examination');
  const [selectedRow, setSelectedRow] = useState<any>(null);
  // Latest selection for async callbacks (list refresh) without stale closures.
  const selectedRowRef = useRef<any>(null);
  selectedRowRef.current = selectedRow;
  // A record another page asked to open (e.g. the Sales dashboard's examination
  // list). Held until the patient's list containing it has loaded: the shared
  // list can still hold the previously viewed patient's records on first run.
  const deepLinkRecordIdRef = useRef<number | string | null>(
    (location.state as { clinicalRecordId?: number | string } | null)
      ?.clinicalRecordId ?? null,
  );
  const [signingOffId, setSigningOffId] = useState<number | null>(null);
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);

  // ------------------------------
  // 📌 PATIENT INFO
  // ------------------------------
  const [patientInfo, setPatientInfo] = useState<IPatientInfo>();

  // ------------------------------
  // 📌 CLINICAL RECORDS (DATA STATES)
  // ------------------------------
  const [selectedClinicalRecord, setSelectedClinicalRecord] = useState<any>({});
  const [desRecord, setDesRecord] = useState<any>({});

  // ------------------------------
  // 📌 EYE EXAMINATION STATES
  // ------------------------------
  const [refraction, setRefraction] = useState({} as IRefractionSchema);
  const [visionData, setVisionData] = useState({
    nearVision: {} as IVisionSchema,
    distanceVision: {} as IVisionSchema,
  });
  const [autoRefractorData, setAutoRefractorData] = useState(
    {} as IAutoRefractorSchema,
  );
  const [examination, setExamination] = useState({} as IExaminationSchema);
  const [handover, setHandover] = useState({} as IHandoverSchema);
  const [history, setHistory] = useState({} as IPatientSymptomsSchema);
  const [anteriorEye, setAnteriorEye] = useState({} as IAnteriorEye);

  // ------------------------------
  // 📌 CONTACT LENS STATES
  // ------------------------------
  const [showCLForm, setShowCLForm] = useState(false);
  const [clHistoryAndSymptoms, setClHistoryAndSymptoms] = useState(
    {} as ICLHistoryAndSymptoms,
  );
  const [clFitting, setClFitting] = useState({} as ICLFitting);
  const [trails, setTrails] = useState<ITrail[]>([]);
  const [clAftercare, setClAftercare] = useState({} as ICLAftercare);
  const [clHandover, setClHandover] = useState({} as ICLHandover);
  const [teachScreen, setTeachScreen] = useState({} as ITeachScreen);

  // ------------------------------
  // 📌 MISC / PAYLOAD
  // ------------------------------
  const [payloadState, setPayloadState] = useState<any>(null);
  const [finalData, setFinalData] = useState({});

  // ------------------------------
  // 📌 NOTES DIALOG STATE
  // ------------------------------
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [notesContent, setNotesContent] = useState<string>('');

  // ======================================================
  // 🟦 EFFECT: SHOW NOTES DIALOG IF NAV STATE REQUESTED
  // ======================================================
  useEffect(() => {
    const navState: any = location?.state;
    if (
      navState?.showNotes &&
      typeof navState?.notes === 'string' &&
      navState.notes.trim().length > 0
    ) {
      setNotesContent(navState.notes);
      const timer = setTimeout(() => setOpenNotesDialog(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  const handleCloseNotesDialog = () => setOpenNotesDialog(false);

  // ======================================================
  // 🟩 EFFECT: LOAD PATIENT INFO
  // ======================================================
  useEffect(() => {
    if (patientId) {
      const fetchPatientInfo = async () => {
        try {
          const patientService = new PatientService();
          const response = await patientService.getPatientName(patientId);
          setPatientInfo(response.data);
        } catch (error) {
          console.error('Error fetching patient data:', error);
        }
      };
      fetchPatientInfo();
    }
  }, [patientId]);
  // ======================================================
  // 🟧 EFFECT: LOAD CLINICAL RECORDS
  // ======================================================
  useEffect(() => {
    void refreshClinicalRecords();
  }, [refreshClinicalRecords]);

  // ======================================================
  // 🔧 HANDLERS
  // ======================================================
  const handlePayloadUpdate = (payload: any) => {
    setPayloadState(payload);
  };

  const handleChildChange = (event: React.SyntheticEvent, newValue: string) =>
    setEyeExamination(newValue);

  const handleContactLensChange = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => setContactLenses(newValue);

  const handleDiabeticEyeScreeningChange = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => setDiabeticEyeScreening(newValue);

  /** Single place to switch pathway; keeps state consistent across Eye / CL / DES. */
  const handleClinicalRecordTypeChange = useCallback((type: string) => {
    setSelectedType(type);
    setSelectedRow(null);
    setSelectedClinicalRecord({});
    setDesRecord({});
    setMecsRecord({});
    setGrrRecord({});
    setEyeExamination('info');
    setContactLenses('info');
    setDiabeticEyeScreening('info');
    setHistory({} as IPatientSymptomsSchema);
    setExamination({} as IExaminationSchema);
    setRefraction({} as IRefractionSchema);
    setHandover({} as IHandoverSchema);
  }, []);

  const handleTabChange = (tabId: string) => {
    setDiabeticEyeScreening(tabId);
  };

  const setNewClinicalRecordId = (id: string) => {
    if (selectedRow !== id) {
      setSelectedRow(id);
    }
  };

  const getTabLabel = (tab: any, type: string) => {
    if (
      !selectedClinicalRecord ||
      !clinicalRecords ||
      tab.id === 'info' ||
      tab.id === 'communications' ||
      tab.id === 'nhs_claims'
    )
      return tab.label;

    const currentRecord = (clinicalRecords as IClinicalRecordSchema[]).find(
      (r) => r.id === selectedRow,
    );
    if (!currentRecord) return tab.label;

    let hasData = false;
    const record = currentRecord as any;
    if (type === 'eye_examination') {
      switch (tab.id) {
        case 'prescreening':
          hasData = !!record.prescreeningId;
          break;
        case 'history_and_symptoms':
          hasData = !!record.symptomsId;
          break;
        case 'refraction':
          hasData = !!record.refractionId;
          break;
        case 'examination':
          hasData = !!record.examinationId;
          break;
        default:
          break;
      }
    } else if (type === 'contact_lenses') {
      switch (tab.id) {
        case 'cl_h_and_s':
          hasData = !!record.clHistoryAndSymptomsId;
          break;
        case 'anterior_eye':
          hasData = !!record.anteriorEyeId;
          break;
        case 'cl_fitting':
          hasData = !!record.contactLensFittingId;
          break;
        case 'cl_aftercare':
          hasData = !!record.contactLensAfterCareId;
          break;
        case 'teach_screen':
          hasData = !!record.teachScreenId;
          break;
        default:
          break;
      }
    } else if (type === 'diabetic_eye_screening') {
      switch (tab.id) {
        case 'des':
          hasData = !!record.diabeticEyeScreeningId;
          break;
        case 'mecs':
          hasData = !!record.mecsCuesId;
          break;
        case 'grr':
          hasData = !!record.glaucomaReferralRefinementId;
          break;
        default:
          break;
      }
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="inherit">{tab.label}</Typography>
        <Typography variant="inherit" sx={{ ml: 0.5 }}>
          {hasData ? '✓' : '○'}
        </Typography>
      </Stack>
    );
  };

  const showEyeExaminationTabs = (tabId: string = eyeExamination) => {
    switch (tabId) {
      case EYE_EXAMINATION_TABS[0].id:
        return (
          patientInfo && (
            <PatientInfo patientInfo={patientInfo} patientId={patientId} />
          )
        );
      case EYE_EXAMINATION_TABS[1].id:
        return (
          <PatientPrescreening
            tab={(tab: string) => setEyeExamination(tab)}
            clinicalRecordId={selectedRow}
            data={selectedClinicalRecord}
            setNewClinicalId={setNewClinicalRecordId}
            onUpdate={refreshClinicalRecords}
            onUpdatePayload={handlePayloadUpdate}
            setShowCLForm={(showCLForm: boolean) => setShowCLForm(showCLForm)}
            setHistory={(history: IPatientSymptomsSchema) =>
              setHistory(history)
            }
          />
        );
      case EYE_EXAMINATION_TABS[2].id:
        return (
          <AddHistoryAndSymptoms
            history={history}
            clinicalRecordId={selectedRow}
            tab={(tab: string) => setEyeExamination(tab)}
            getAllHistoryAndSymptoms={getHistoryRecords}
            onUpdate={refreshClinicalRecords}
            setHistory={(data: IPatientSymptomsSchema) => {
              setHistory(data);
            }}
          />
        );
      case EYE_EXAMINATION_TABS[3].id:
        return (
          <Refraction
            tab={(tab: string) => setEyeExamination(tab)}
            selectedRefraction={refraction}
            clinicalRecordId={selectedRow}
            getAllRefraction={getRefractionRecords}
            onUpdate={refreshClinicalRecords}
            visionData={visionData}
            autoRefractorData={autoRefractorData}
            setRefraction={(data: IRefractionSchema) => {
              setRefraction(data);
            }}
          />
        );
      case EYE_EXAMINATION_TABS[4].id:
        return (
          <Examination
            setTab={(tab: string) => setEyeExamination(tab)}
            clinicalRecordId={selectedRow}
            selectedExamination={examination}
            prescreeningData={selectedClinicalRecord}
            getAllExamination={getExaminationRecords}
            onUpdate={refreshClinicalRecords}
            finalData={finalData}
            setExamination={(data: IExaminationSchema) => {
              setExamination(data);
            }}
          />
        );
      case EYE_EXAMINATION_TABS[5].id:
        const currentRecord = clinicalRecords.find(
          (record) => record.id === selectedRow,
        );
        return (
          <Handover
            selectedHandover={handover}
            clinicalRecordId={selectedRow}
            clinicalRecord={currentRecord}
            getAllHandover={getHandoverRecords}
            onUpdate={refreshClinicalRecords}
            finalData={finalData}
            setHandover={(data: IHandoverSchema) => {
              setHandover(data);
            }}
          />
        );
      case 'communications':
        return <PatientCommunicationLog patientId={patientId!} />;
      case 'nhs_claims':
        return <PatientNhsClaims patientId={patientId} />;
      default:
        break;
    }
  };

  const showContactLensesTabs = (tabId: string = contactLenses) => {
    switch (tabId) {
      case CONTACT_LENSES_TABS[0].id:
        // return patientInfo && <PatientInfo patientInfo={patientInfo} />;
        return <PatientInfo patientInfo={patientInfo} patientId={patientId} />;
      case CONTACT_LENSES_TABS[1].id:
        return (
          <CLHistoryAndSymptoms
            clHistorySymptoms={clHistoryAndSymptoms}
            showCLForm={showCLForm}
            tab={(tab: string) => setContactLenses(tab)}
            onUpdate={refreshClinicalRecords}
            setClinicalRecordId={(id: number) => setSelectedRow(id)}
            getAllCLHistoryAndSymptoms={getAllCLHistoryAndSymptoms}
            setSelectedRow={(selectedRow: string) =>
              setSelectedRow(selectedRow)
            }
            clinicalRecordId={selectedRow}
            finalData={finalData}
          />
        );
      case CONTACT_LENSES_TABS[2].id:
        return (
          <AnteriorEye
            anteriorEye={anteriorEye}
            clinicalRecordId={selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            showCLForm={showCLForm}
            getAnteriorEyeRecords={getAnteriorEyeRecords}
            onUpdate={refreshClinicalRecords}
            selectedRow={selectedRow}
            clinicalRecords={clinicalRecords}
          />
        );
      case CONTACT_LENSES_TABS[3].id:
        return (
          <CLFitting
            clinicalRecordId={selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            showCLForm={showCLForm}
            clFitting={clFitting}
            trails={trails}
            getCLFittingRecords={getCLFittingRecords}
            getTrailsRecords={getTrailsRecords}
            onUpdate={refreshClinicalRecords}
            selectedRow={selectedRow}
            clinicalRecords={clinicalRecords}
          />
        );
      case CONTACT_LENSES_TABS[4].id:
        return (
          <CLAftercare
            clinicalRecordId={selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            clAftercare={clAftercare}
            showCLForm={showCLForm}
            getCLAftercareRecords={getCLAftercareRecords}
            onUpdate={refreshClinicalRecords}
            selectedRow={selectedRow}
            clinicalRecords={clinicalRecords}
            trails={trails}
          />
        );
      case CONTACT_LENSES_TABS[5].id:
        const currentRecord = clinicalRecords.find(
          (record) => record.id === selectedRow,
        );
        return (
          <CLHandover
            clinicalRecordId={selectedRow}
            tab={(tab: string) => setContactLenses(tab)}
            clHandover={clHandover}
            showCLForm={showCLForm}
            getCLHandoverRecords={getCLHandoverRecords}
            clinicalRecord={currentRecord}
            onUpdate={refreshClinicalRecords}
            finalData={finalData}
          />
        );
      case CONTACT_LENSES_TABS[6].id:
        return (
          <TeachScreen
            clinicalRecordId={selectedRow}
            teachScreen={teachScreen}
            showCLForm={showCLForm}
            getTeachScreenRecords={getTeachScreenRecords}
            onUpdate={refreshClinicalRecords}
          />
        );
      case 'communications':
        return <PatientCommunicationLog patientId={patientId!} />;
      default:
        break;
    }
  };

  // function to show different tab data
  const showDiabeticEyeScreeningTabs = (
    tabId: string = diabeticEyeScreening,
  ) => {
    switch (tabId) {
      case CLINICAL_PATHWAY_TABS[0].id:
        return (
          patientInfo && (
            <PatientInfo patientInfo={patientInfo} patientId={patientId} />
          )
        );

      case CLINICAL_PATHWAY_TABS[1].id:
        return (
          <DiabeticEyeScreeningForm
            clinicalRecordId={selectedRow}
            desRecord={desRecord}
            onUpdate={refreshClinicalRecords}
          />
        );

      case CLINICAL_PATHWAY_TABS[2].id:
        return (
          <MecsCuesForm
            clinicalRecordId={selectedRow}
            mecsRecord={mecsRecord}
            onUpdate={refreshClinicalRecords}
          />
        );

      case CLINICAL_PATHWAY_TABS[3].id:
        return (
          <GlaucomaReferralRefinementForm
            clinicalRecordId={selectedRow}
            grrRecord={grrRecord}
            onUpdate={refreshClinicalRecords}
          />
        );

      // case CLINICAL_PATHWAY_TABS[4].id:
      //   return <ContactLensTeachingForm />;

      case CLINICAL_PATHWAY_TABS[4].id:
        const currentRecord = clinicalRecords.find(
          (record) => record.id === selectedRow,
        );
        return (
          <HandoverOutcomeForm
            clinicalRecordId={selectedRow}
            desRecord={desRecord}
            onUpdate={refreshClinicalRecords}
            clinicalRecord={currentRecord}
          />
        );

      case 'communications':
        return <PatientCommunicationLog patientId={patientId!} />;
      default:
        return null;
    }
  };

  const getAllCLHistoryAndSymptoms = async (
    clHistoryId: number | null,
    clinicalId: number,
  ) => {
    if (clHistoryId === null) {
      setClHistoryAndSymptoms({} as ICLHistoryAndSymptoms);
      return;
    }
    const clHistoryAndSymptomsService = new CLHistoryAndSymptomsService();

    try {
      const res = await clHistoryAndSymptomsService.getHistoryAndSymptoms(
        patientId!,
        clHistoryId,
      );
      const data = res.data[0];
      setClHistoryAndSymptoms(data);
    } catch (error) {
      console.error('Error fetching CL history and symptoms:', error);
      setClHistoryAndSymptoms({} as ICLHistoryAndSymptoms);
      return false;
    }
  };

  const getAnteriorEyeRecords = async (
    anteriorEyeId: number | null,
    clinicalId: number,
  ) => {
    if (anteriorEyeId === null) {
      setAnteriorEye({} as IAnteriorEye);
      return;
    }
    const anteriorEyeService = new AnteriorEyeService();

    try {
      const res = await anteriorEyeService.getAnteriorEye(
        patientId!,
        anteriorEyeId,
      );
      const data = res.data[0];
      setAnteriorEye(data);
    } catch (error) {
      console.error('Error fetching CL anteriorEye:', error);
      setAnteriorEye({} as IAnteriorEye);
      return false;
    }
  };

  const getCLFittingRecords = async (
    clFittingId: number | null,
    clinicalId: number,
  ) => {
    if (clFittingId === null) {
      setClFitting({} as ICLFitting);
      return;
    }

    const clFittingService = new CLFittingService();

    try {
      const res = await clFittingService.getCLFittings(patientId!, clFittingId);
      const data = res.data[0];
      setClFitting(data);
    } catch (error) {
      console.error('Error fetching CL fitting records:', error);
      setClFitting({} as ICLFitting);
      return false;
    }
  };

  const getTrailsRecords = async (clinicalId: number, type?: string) => {
    if ((type ?? selectedType) === 'contact_lenses') {
      const trailsService = new TrailsService();

      try {
        const res = await trailsService.getTrails(patientId!, clinicalId);
        const data = res.data;
        setTrails(data);
      } catch (error) {
        console.error('Error fetching trails records:', error);
        setTrails([]);
        return false;
      }
    }
  };

  const getCLAftercareRecords = async (
    clAftercareId: number | null,
    clinicalId: number,
  ) => {
    if (clAftercareId === null) {
      setClAftercare({} as ICLAftercare);
      return;
    }
    const clAftercareService = new CLAftercareService();

    try {
      const res = await clAftercareService.getCLAftercare(
        patientId!,
        clAftercareId,
      );
      const data = res.data[0];
      setClAftercare(data);
    } catch (error) {
      console.error('Error fetching CL aftercare records:', error);
      setClAftercare({} as ICLAftercare);
      return false;
    }
  };

  const getCLHandoverRecords = async (
    clHandoverId: number | null,
    clinicalId: number,
  ) => {
    if (clHandoverId === null) {
      setClHandover({} as ICLHandover);
      return;
    }
    const clHandoverService = new CLHandoverService();

    try {
      const res = await clHandoverService.getCLHandover(
        patientId!,
        clHandoverId,
      );
      const data = res.data[0];
      setClHandover(data);
    } catch (error) {
      console.error('Error fetching CL handover records:', error);
      setClHandover({} as ICLHandover);
      return false;
    }
  };

  const getTeachScreenRecords = async (
    teachScreenId: number | null,
    clinicalId: number,
  ) => {
    if (teachScreenId === null) {
      setTeachScreen({} as ITeachScreen);
      return;
    }
    const teachScreenService = new TeachScreenService();

    try {
      const res = await teachScreenService.getTeachScreen(
        patientId!,
        teachScreenId,
      );
      const data = res.data[0];
      setTeachScreen(data);
    } catch (error) {
      console.error('Error fetching teach screen records:', error);
      setTeachScreen({} as ITeachScreen);
      return false;
    }
  };

  const getDESRecords = async (desId: number | null, clinicalId: number) => {
    if (desId === null) {
      setDesRecord({});
      return;
    }
    const desService = new DEService();
    try {
      const res = await desService.getDES(patientId!, desId);
      const data = res.data[0];
      setDesRecord(data || {});
    } catch (error) {
      console.error('Error fetching DES records:', error);
      setDesRecord({});
      return false;
    }
  };

  const [mecsRecord, setMecsRecord] = useState<any>({});
  const [grrRecord, setGrrRecord] = useState<any>({});

  const getMECSRecords = async (mecsId: number | null, clinicalId: number) => {
    if (mecsId === null) {
      setMecsRecord({});
      return;
    }
    const mecsService = new MecsCuesService();
    try {
      const res = await mecsService.getMecs(patientId!, mecsId);
      const data = res.data[0];
      setMecsRecord(data || {});
    } catch (error) {
      console.error('Error fetching MECS/CUES records:', error);
      setMecsRecord({});
      return false;
    }
  };

  const getGRRRecords = async (grrId: number | null, clinicalId: number) => {
    if (grrId === null) {
      setGrrRecord({});
      return;
    }
    const grrService = new GlaucomaReferralRefinementService();
    try {
      const res = await grrService.get(patientId!, grrId);
      const data = res.data[0];
      setGrrRecord(data || {});
    } catch (error) {
      console.error('Error fetching GRR records:', error);
      setGrrRecord({});
      return false;
    }
  };

  const getClinicalRecord = useCallback(
    async (prescreeningId: number | null, clinicalId: number) => {
      // Clear values derived from the previously opened record's prescreening.
      setVisionData({
        nearVision: {} as IVisionSchema,
        distanceVision: {} as IVisionSchema,
      });
      setAutoRefractorData({} as IAutoRefractorSchema);
      if (prescreeningId === null) {
        setSelectedClinicalRecord({});
        return;
      }
      const clinicalRecords = new ClinicalRecords();

      try {
        const res = await clinicalRecords.getPrescreeningById(
          patientId!,
          prescreeningId,
        );
        const data = res.data[0];

        setAutoRefractorData(data?.autoRefractor);
        const specsHistory = data?.specificationHistories;
        if (specsHistory) {
          specsHistory.forEach((spec: any) => {
            if (spec?.specifications === 'SVN') {
              setVisionData((prevData) => ({
                ...prevData,
                nearVision: {
                  rightSphericalPower: spec?.rightSphericalPower,
                  leftSphericalPower: spec?.leftSphericalPower,
                  rightCylinder: spec?.rightCylinder,
                  leftCylinder: spec?.leftCylinder,
                  rightAxis: spec?.rightAxis,
                  leftAxis: spec?.leftAxis,
                  rightDistancePD: spec?.rightDistancePD,
                  leftDistancePD: spec?.leftDistancePD,
                  rightBVD: spec?.rightBVD,
                  leftBVD: spec?.leftBVD,
                },
              }));
            } else if (spec?.specifications === 'SVD') {
              setVisionData((prevData) => ({
                ...prevData,
                distanceVision: {
                  rightSphericalPower: spec?.rightSphericalPower,
                  leftSphericalPower: spec?.leftSphericalPower,
                  rightCylinder: spec?.rightCylinder,
                  leftCylinder: spec?.leftCylinder,
                  rightAxis: spec?.rightAxis,
                  leftAxis: spec?.leftAxis,
                  rightDistancePD: spec?.rightDistancePD,
                  leftDistancePD: spec?.leftDistancePD,
                  rightBVD: spec?.rightBVD,
                  leftBVD: spec?.leftBVD,
                },
              }));
            }
          });
        }
        setSelectedClinicalRecord(data);
      } catch (error) {
        console.error('Error fetching Prescreening Records:', error);
        setSelectedClinicalRecord({});
        return false;
      }
    },
    [patientId],
  );

  /**
   * After the record list reloads (initial load or after a save), reload the
   * sections of the record that is currently OPEN, not always the newest one,
   * so a refresh never overwrites the record the user clicked.
   */
  const getAllClinicsRecords = useCallback(async () => {
    if (!clinicalRecords || clinicalRecords.length === 0) {
      return; // Wait for clinical records to be loaded
    }

    const wantedId = deepLinkRecordIdRef.current ?? selectedRowRef.current;
    const wanted: any = clinicalRecords.find(
      (item: any) => String(item.id) === String(wantedId),
    );
    if (wanted && deepLinkRecordIdRef.current !== null) {
      deepLinkRecordIdRef.current = null;
    }
    const record: any = wanted ?? clinicalRecords[0];
    const clinicalId = record.id;

    if (selectedRowRef.current !== clinicalId) {
      setSelectedRow(clinicalId);
    }

    await trackRecordLoad(() =>
      Promise.all([
        getClinicalRecord(record.prescreeningId ?? null, clinicalId),
        getHistoryRecords(record.symptomsId ?? null, clinicalId),
        getRefractionRecords(record.refractionId ?? null, clinicalId),
        getExaminationRecords(record.examinationId ?? null, clinicalId),
        getHandoverRecords(record.handoverId ?? null, clinicalId),
        getDESRecords(record.diabeticEyeScreeningId ?? null, clinicalId),
        getMECSRecords(record.mecsCuesId ?? null, clinicalId),
        getGRRRecords(record.glaucomaReferralRefinementId ?? null, clinicalId),
      ]),
    );
    // Section getters are plain functions recreated each render; the list and
    // the selection ref are what matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalRecords, getClinicalRecord, trackRecordLoad]);

  const getRefractionRecords = async (
    refractionId: number | null,
    clinicalId: number,
  ) => {
    if (refractionId === null) {
      setRefraction({} as IRefractionSchema);
      setFinalData({});
      return;
    }
    const refractionService = new ClinicalRecords();

    try {
      const res = await refractionService.getRefractionById(
        patientId!,
        refractionId,
      );
      const data = res.data[0];
      setFinalData(data?.final);
      setRefraction(data);
    } catch (error) {
      console.error('Error fetching Refraction Records:', error);
      setRefraction({} as IRefractionSchema);
      setFinalData({});
      return false;
    }
  };

  const getExaminationRecords = async (
    examinationId: number | null,
    clinicalId: number,
  ) => {
    if (examinationId === null) {
      setExamination({} as IExaminationSchema);
      return;
    }
    const clinicalRecords = new ClinicalRecords();

    try {
      const res = await clinicalRecords.getExaminationByClinicalRecordId(
        patientId!,
        examinationId,
      );
      const data = res.data[0];
      setExamination(data);
    } catch (error) {
      console.error('Error fetching Examination:', error);
      setExamination({} as IExaminationSchema);
      return false;
    }
  };

  const getHandoverRecords = async (
    handoverId: number | null,
    clinicalId: number,
  ) => {
    if (handoverId === null) {
      setHandover({} as IHandoverSchema);
      return;
    }
    const handoverRecords = new HandoverService();

    try {
      const res = await handoverRecords.getAllHandoverByClinicalRecordId(
        patientId!,
        handoverId,
      );
      const data = res.data[0];
      setHandover(data);
    } catch (error) {
      console.error('Error fetching Handover:', error);
      setHandover({} as IHandoverSchema);
      return false;
    }
  };

  const getHistoryRecords = async (
    symptomsId: number | null,
    clinicalId: number,
  ) => {
    if (symptomsId === null) {
      setHistory({} as IPatientSymptomsSchema);
      return;
    }
    const historyRecord = new HistoryAndSymptomsService();

    try {
      const res = await historyRecord.getHistoryAndSymptoms(
        patientId!,
        symptomsId,
      );
      setHistory(res.data);
    } catch (error) {
      console.error('Error fetching H&S:', error);
      setHistory({} as IPatientSymptomsSchema);
      return false;
    }
  };

  /**
   * Opens a record: selects it, switches to its pathway and loads every
   * section form's data. Loading stays on until ALL sections have loaded, and
   * a failed section is reported instead of silently showing an empty form.
   * Works for records with only some sections (missing ids load as empty).
   */
  const openRecord = async (item: any) => {
    const seq = ++openSeqRef.current;

    let recordType = selectedType;
    if (item.type === 'Contact Lenses') {
      recordType = 'contact_lenses';
    } else if (item.type === 'Eye Examination') {
      recordType = 'eye_examination';
    } else if (
      item.type === 'Diabetic Eye Screening' ||
      item.type === 'MECS/CUES' ||
      item.type === 'Glaucoma Referral Refinement'
    ) {
      recordType = 'diabetic_eye_screening';
    }
    setSelectedType(recordType);
    setSelectedRow(item.id);

    const id = item.id;
    const results = await trackRecordLoad(() =>
      Promise.all([
        getClinicalRecord(item.prescreeningId ?? null, id),
        getRefractionRecords(item.refractionId ?? null, id),
        getHistoryRecords(item.symptomsId ?? null, id),
        getExaminationRecords(item.examinationId ?? null, id),
        getHandoverRecords(item.handoverId ?? null, id),
        getAnteriorEyeRecords(item.anteriorEyeId ?? null, id),
        getAllCLHistoryAndSymptoms(item.clHistoryAndSymptomsId ?? null, id),
        getCLFittingRecords(item.contactLensFittingId ?? null, id),
        getTrailsRecords(id, recordType),
        getCLAftercareRecords(item.contactLensAfterCareId ?? null, id),
        getCLHandoverRecords(item.contactLensHandoverId ?? null, id),
        getTeachScreenRecords(item.teachScreenId ?? null, id),
        getDESRecords(item.diabeticEyeScreeningId ?? null, id),
        getMECSRecords(item.mecsCuesId ?? null, id),
        getGRRRecords(item.glaucomaReferralRefinementId ?? null, id),
      ]),
    );
    // A newer click (or the post-save refresh) has taken over: its result is
    // the one on screen, so don't report this one's failures over it.
    if (seq !== openSeqRef.current) return;
    if (results.some((ok) => ok === false)) {
      enqueueSnackbar(
        `Some sections of clinical record #${id} could not be loaded. Please try again.`,
        { variant: 'error' },
      );
    }
  };

  // Amendment lock (see components/clinical-record-lock). The ref lets event
  // handlers here check for unsigned amendment changes before switching record.
  const lockRef = useRef<ClinicalRecordLockValue | null>(null);
  const confirmDiscardAmendment = () =>
    lockRef.current?.confirmDiscard() ?? true;
  const lockRecordId = selectedRow;
  const lockRecord: any =
    clinicalRecords.find(
      (record: any) => String(record.id) === String(lockRecordId),
    ) ?? null;
  const handleAmendmentCommitted = async () => {
    const records: any[] = (await refreshClinicalRecords()) ?? [];
    const fresh = records.find(
      (record) => String(record.id) === String(lockRecordId),
    );
    if (fresh) await openRecord(fresh);
  };

  const handleSignOffConfirm = async (signatureData: string) => {
    if (!selectedRow) return;
    setSigningOffId(selectedRow);
    try {
      const service = new ClinicalRecords();
      await service.signOff(selectedRow, signatureData);
      enqueueSnackbar('Clinical record signed off successfully', {
        variant: 'success',
      });
      await refreshClinicalRecords();
      setOpenSignatureDialog(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to sign off clinical record', {
        variant: 'error',
      });
    } finally {
      setSigningOffId(null);
    }
  };

  // const getPatientInfo = (patientId: any) => {
  //   const patientService = new PatientService();
  //   patientService.getPatientName(patientId).then((res) => {
  //     setPatientInfo(res.data);
  //     console.log("Res: ",res);
  //   });
  // };

  useEffect(() => {
    if (clinicalRecords && clinicalRecords.length > 0) {
      getAllClinicsRecords();
      // getPatientInfo(patientId);
    }
  }, [clinicalRecords, getAllClinicsRecords, patientId]);
  return (
    <ClinicalRecordLockProvider
      record={lockRecord}
      onCommitted={handleAmendmentCommitted}
    >
      <ClinicalRecordLockBridge lockRef={lockRef} />
      <Stack
        alignItems="center"
        direction={{
          xs: 'column',
          md: 'row',
        }}
        sx={{
          borderRadius: '15px',
          position: 'relative',
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
        }}
        p={2}
      >
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 'auto' }}>
            <Box
              sx={{
                p: 1,
                display: 'flex',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  pl: 2,
                  pr: 1,
                  flexGrow: 1,
                  width: '20%',
                  mt: '20px',
                }}
              >
                <Stack spacing={0.1} direction="column" gap={2}>
                  <Typography variant="body1" noWrap>
                    {patientInfo?.firstName}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ color: 'text.secondary' }}
                  >
                    {patientInfo?.postCode}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    noWrap
                    sx={{ color: 'text.secondary' }}
                  >
                    {fDate(patientInfo?.date_of_birth ?? null)}
                  </Typography>
                </Stack>
              </Box>
              <Box
                sx={{
                  borderRadius: '15px',
                  boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16)',
                  maxHeight: '200px',
                  width: '80%',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  position: 'relative',
                }}
              >
                {(isRefreshingRecords || isRecordLoading) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255,255,255,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
                <Table sx={{ minWidth: 'auto' }}>
                  <TableHeadCustom headLabel={TABLE_HEAD} />
                  <TableBody>
                    {clinicalRecords.map((item: any) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor:
                            selectedRow === item.id ? '#e0e0e0' : 'inherit',
                        }}
                        onClick={() => {
                          if (
                            item.id !== selectedRow &&
                            !confirmDiscardAmendment()
                          )
                            return;
                          setShowCLForm(false);
                          void openRecord(item);
                        }}
                      >
                        <TableCell align="left">
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <CustomAvatar name={`${item.id}`} />
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          {item?.type === 'Diabetic Eye Screening'
                            ? 'Diabetic Screening'
                            : item?.type === 'MECS/CUES'
                              ? 'MECS/CUES'
                              : item?.type === 'Glaucoma Referral Refinement'
                                ? 'Glaucoma Refinement'
                                : formatVisitReasonDisplay(
                                    item?.prescreening?.reasonForVisit,
                                  ) ||
                                  formatVisitReasonDisplay(
                                    item?.contactLensHistoryAndSymptoms
                                      ?.reasonForVisit,
                                  ) ||
                                  formatVisitReasonDisplay(
                                    item?.patientSymptoms?.reasonForVisit,
                                  ) ||
                                  'N/A'}
                        </TableCell>
                        <TableCell align="left">
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <span>{item.type}</span>
                            {item.isAmended ? (
                              <Label color="warning" variant="soft">
                                Amended
                              </Label>
                            ) : item.signedAt ? (
                              <Label color="success" variant="soft">
                                Signed
                              </Label>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          {formatDate(item.createdDate)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableNoData isNotFound={clinicalRecords.length <= 0} />
                  </TableBody>
                </Table>
              </Box>
            </Box>
            {(isRefreshingRecords || isRecordLoading) && (
              <Box sx={{ px: 2, pb: 1 }}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">
                  Updating clinical record history and completion status...
                </Typography>
              </Box>
            )}
            <Box
              sx={(theme) => ({
                width: '100%',
                flexShrink: 0,
                px: { xs: 1.5, sm: 2 },
                pt: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                bgcolor: theme.palette.background.paper,
                position: 'sticky',
                top: 0,
                zIndex: theme.zIndex.appBar - 1,
              })}
            >
              <ClinicalRecordTypeSelect
                variant="embedded"
                selectedType={selectedType}
                setSelectedType={(type: string) => {
                  if (!confirmDiscardAmendment()) return;
                  handleClinicalRecordTypeChange(type);
                }}
              />
            </Box>
            <Scrollbar>
              {lockRecord && (
                <Box sx={{ px: 2, pt: 2 }}>
                  <ClinicalRecordStatusBar
                    signedByName={
                      lockRecord.signedByUser?.displayName ??
                      lockRecord.signedByUser?.username
                    }
                    signedAt={lockRecord.signedAt}
                    lastAmendedByName={
                      lockRecord.lastAmendedByUser?.displayName
                    }
                    lastAmendedAt={lockRecord.lastAmendedAt}
                  />
                </Box>
              )}
              {selectedType === 'eye_examination' && (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1 }}
                  >
                    <Tabs value={eyeExamination} onChange={handleChildChange}>
                      {EYE_EXAMINATION_TABS.map((head) => (
                        <Tab
                          key={head.id}
                          value={head.id}
                          label={getTabLabel(head, 'eye_examination')}
                          sx={{ ml: '80px' }}
                        />
                      ))}
                    </Tabs>
                  </Stack>
                  <ClinicalTabPanels
                    tabs={EYE_EXAMINATION_TABS}
                    activeTab={eyeExamination}
                    renderTab={showEyeExaminationTabs}
                    handoverTabs={{ handover: !!lockRecord?.handoverId }}
                  />
                </>
              )}
              {selectedType === 'contact_lenses' && (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1 }}
                  >
                    <Tabs
                      value={contactLenses}
                      onChange={handleContactLensChange}
                    >
                      {CONTACT_LENSES_TABS.map((head) => (
                        <Tab
                          key={head.id}
                          value={head.id}
                          label={getTabLabel(head, 'contact_lenses')}
                          sx={{ ml: '80px' }}
                        />
                      ))}
                    </Tabs>
                  </Stack>
                  <ClinicalTabPanels
                    tabs={CONTACT_LENSES_TABS}
                    activeTab={contactLenses}
                    renderTab={showContactLensesTabs}
                    handoverTabs={{
                      cl_handover: !!lockRecord?.contactLensHandoverId,
                    }}
                  />
                </>
              )}
              {selectedType === 'diabetic_eye_screening' && (
                <>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1 }}
                  >
                    <Tabs
                      value={diabeticEyeScreening}
                      onChange={handleDiabeticEyeScreeningChange}
                    >
                      {CLINICAL_PATHWAY_TABS.map((head) => (
                        <Tab
                          key={head.id}
                          value={head.id}
                          label={getTabLabel(head, 'diabetic_eye_screening')}
                          sx={{ ml: '80px' }}
                        />
                      ))}
                    </Tabs>
                  </Stack>
                  <ClinicalTabPanels
                    tabs={CLINICAL_PATHWAY_TABS}
                    activeTab={diabeticEyeScreening}
                    renderTab={showDiabeticEyeScreeningTabs}
                    handoverTabs={{
                      handover_outcome: !!(desRecord as any)
                        ?.handoverCompletedAt,
                    }}
                  />
                </>
              )}
            </Scrollbar>
          </Table>
        </Box>
        {/* Signature Dialog */}
        <SignatureDialog
          open={openSignatureDialog}
          onClose={() => setOpenSignatureDialog(false)}
          onConfirm={handleSignOffConfirm}
          loading={!!signingOffId}
        />

        {/* Delayed Patient Notes Dialog */}
        <Dialog
          open={openNotesDialog}
          onClose={handleCloseNotesDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Patient Notes</DialogTitle>
          <DialogContent>
            <Typography>{notesContent}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNotesDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </ClinicalRecordLockProvider>
  );
}
